/**
 * Cấp cửa hàng, mở khóa, lương nhân viên và quầy cà phê.
 *
 * M3: nhân viên phải THUÊ (phí một lần P37/P38) mới tính lương; quầy cà phê
 * cần cửa hàng cấp 3 + người chơi cấp 3 (mở Bãi biển), chi tiết 5 món;
 * nâng cấp lên chi nhánh cần cấp người chơi mở khu tương ứng.
 */
import { heSoCau } from './gia.js';
import { nguongNhap } from './kho.js';
import { capNguoiChoiToiThieuDeNangCap, layKhuVuc, lyDoKhongNangCapDuoc } from './khu-vuc.js';
import { heSoThoiTietCaPhe } from './thoi-tiet.js';
import { layThamSo } from './du-lieu.js';
import type { CapCuaHang, DuLieuGame, TrangThaiGame } from './types.js';

/** Thông tin cấp cửa hàng hiện tại. */
export function capHienTai(state: TrangThaiGame, duLieu: DuLieuGame): CapCuaHang {
  const cap = duLieu.capCuaHang[state.capCuaHang - 1];
  if (!cap) throw new Error(`Cấp cửa hàng lạ: ${state.capCuaHang}.`);
  return cap;
}

/** Chi phí nâng lên cấp tiếp theo; null nếu đã cấp cuối. */
export function chiPhiNangCap(state: TrangThaiGame, duLieu: DuLieuGame): number | null {
  if (state.capCuaHang >= duLieu.capCuaHang.length) return null;
  return duLieu.capCuaHang[state.capCuaHang]?.chiPhiLenCap ?? null;
}

/** Có thể nâng cấp không (chưa cấp cuối, đủ tiền, đủ cấp người chơi mở khu). */
export function coTheNangCap(state: TrangThaiGame, duLieu: DuLieuGame): boolean {
  const chiPhi = chiPhiNangCap(state, duLieu);
  return chiPhi !== null && state.xu >= chiPhi && lyDoKhongNangCapDuoc(state, duLieu) === null;
}

/**
 * Nâng cấp cửa hàng: trừ tiền, tăng cấp, nhập đầy ngưỡng MIỄN PHÍ cho món
 * mới mở (nhà cung cấp hỗ trợ mở quầy mới — khớp giả định bảng tính rằng
 * lên cấp là có ngay đủ hàng đạt lãi suất cấp mới), ghi mốc thời gian.
 * M3: lên cấp 4/5 (chi nhánh) cần người chơi đạt cấp mở khu tương ứng.
 * Trả về false nếu không nâng được.
 */
export function nangCap(state: TrangThaiGame, duLieu: DuLieuGame): boolean {
  const chiPhi = chiPhiNangCap(state, duLieu);
  if (chiPhi === null || state.xu < chiPhi) return false;
  if (lyDoKhongNangCapDuoc(state, duLieu) !== null) return false;
  state.xu -= chiPhi;
  state.thongKe.tongChiNangCap += chiPhi;
  state.capCuaHang += 1;
  for (const m of duLieu.matHang.filter((x) => x.capMo === state.capCuaHang)) {
    state.kho[m.ma] = { ton: nguongNhap(m.ma, state, duLieu) };
  }
  state.mocCap.push({ cap: state.capCuaHang, gioThuc: state.tongGioGame / 60 });
  return true;
}

/** Cấp người chơi tối thiểu để nâng lên cấp tiếp theo (hiển thị UI). */
export function capNguoiChoiCanDeNangCap(state: TrangThaiGame, duLieu: DuLieuGame): number {
  return capNguoiChoiToiThieuDeNangCap(state, duLieu);
}

/**
 * Thuê nhân viên (M3): phí một lần P37/P38, cần đạt cấp thuê của vai.
 * Ném lỗi tiếng Việt nếu không thuê được.
 */
export function thueNhanVien(
  state: TrangThaiGame,
  duLieu: DuLieuGame,
  vai: 'thuNgan' | 'xepKe',
): void {
  const daThue = vai === 'thuNgan' ? state.nhanVien.thuNgan : state.nhanVien.xepKe;
  if (daThue) throw new Error('Đã thuê vai này rồi.');
  const thongTin = vai === 'thuNgan' ? duLieu.luong.thuNgan : duLieu.luong.xepKe;
  if (state.capCuaHang < thongTin.capThue) {
    const ten = vai === 'thuNgan' ? 'Thu ngân' : 'Xếp kệ';
    throw new Error(`${ten} thuê được từ cấp cửa hàng ${thongTin.capThue}.`);
  }
  const phi = layThamSo(duLieu, vai === 'thuNgan' ? 'P37' : 'P38');
  if (state.xu < phi) {
    throw new Error(`Cần ${phi} xu để thuê (đang có ${Math.floor(state.xu)} xu).`);
  }
  state.xu -= phi;
  if (vai === 'thuNgan') state.nhanVien.thuNgan = true;
  else state.nhanVien.xepKe = true;
}

/** Tổng lương nhân viên mỗi giờ game — chỉ tính vai ĐÃ THUÊ (M3). */
export function luongMoiGioGame(state: TrangThaiGame, duLieu: DuLieuGame): number {
  let luong = 0;
  if (state.nhanVien.thuNgan) luong += duLieu.luong.thuNgan.luongMoiGioGame;
  if (state.nhanVien.xepKe) luong += duLieu.luong.xepKe.luongMoiGioGame;
  return luong;
}

export interface KetQuaCaPhe {
  coc: number;
  lai: number;
  /** Số cốc theo tên món (hiển thị + thành tựu + nhiệm vụ). */
  theoMon: Record<string, number>;
}

/** Quầy cà phê đã mở chưa: cửa hàng cấp 3 + người chơi cấp 3 (mở Bãi biển). */
export function caPheDaMo(state: TrangThaiGame, duLieu: DuLieuGame): boolean {
  if (!capHienTai(state, duLieu).coCaPhe) return false;
  return state.nguoiChoi.cap >= layKhuVuc(duLieu, 'bai-bien').capNguoiChoiMo;
}

/**
 * Quầy cà phê M3 (đầy đủ): 5 món cố định theo trọng số, lãi gộp 40%,
 * lưu lượng 30% quầy chính — là hệ số lưu lượng, KHÔNG phải mạch kinh tế
 * riêng (master-plan 8). Chịu hệ số cầu theo chính sách, danh tiếng,
 * thời tiết Bãi biển và hiệu suất offline.
 */
export function caPheMoiGio(
  state: TrangThaiGame,
  duLieu: DuLieuGame,
  heSoHieuSuat: number,
): KetQuaCaPhe {
  const rong: KetQuaCaPhe = { coc: 0, lai: 0, theoMon: {} };
  if (!caPheDaMo(state, duLieu)) return rong;
  const cap = capHienTai(state, duLieu);
  const coc =
    (duLieu.caPhe.thongKe.soCocMoiNgayGame *
      cap.heSoLuuLuong *
      state.danhTieng *
      heSoCau(state.chinhSach, duLieu) *
      heSoThoiTietCaPhe(state, duLieu) *
      heSoHieuSuat) /
    24;
  const laiMoiCoc = duLieu.caPhe.thongKe.laiTrungBinhMoiCoc;
  const theoMon: Record<string, number> = {};
  for (const mon of duLieu.trongSoCaPhe.trongSoCaPhe) {
    theoMon[mon.ten] = coc * mon.trongSo;
  }
  return { coc, lai: coc * laiMoiCoc, theoMon };
}
